import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import ThemeToggle from '../components/ThemeToggle';
import { NavUserMenu } from '../pages/ProtectedRoute';
import { useTheme } from '../context/ThemeContext';

const TOOL_GROUPS = [
  {
    label: '🤖 AI Portals',
    items: [
      { to: '/job-search',  label: 'AI Job Search Copilot',  desc: 'Cover letters, job matching' },
      { to: '/freelancer',  label: 'Freelancer AI Toolkit',  desc: 'Upwork proposals, outreach' },
      { to: '/developer',   label: 'Developer AI Tools',     desc: 'SQL, regex, cron, API tester' },
    ],
  },
  {
    label: '🛠️ Free Tools',
    items: [
      { to: '/tools/ip',       label: 'Network Tools',   desc: 'IP lookup, DNS, port scan' },
      { to: '/tools/dev',      label: 'Developer Utils', desc: 'JSON, JWT, UUID, hash' },
      { to: '/tools/text',     label: 'Text Tools',      desc: 'Word count, case converter' },
      { to: '/tools/files',    label: 'File Tools',      desc: 'Image convert, PDF merge' },
      { to: '/tools/business', label: 'Business Tools',  desc: 'Invoice, GST, payroll' },
    ],
  },
  {
    label: '💰 Finance',
    items: [
      { to: '/tools/finance',  label: 'Finance Hub',      desc: 'FIRE, RRSP, TFSA, mortgage' },
      { to: '/tools/ai',       label: 'AI Tools',         desc: 'GPT-powered utilities' },
    ],
  },
];

const TOP_LINKS = [
  { to: '/home',  label: 'Home' },
  { to: '/blog',  label: 'Blog' },
];

export default function Navbar() {
  const [scrolled,     setScrolled]     = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileTools,  setMobileTools]  = useState(false);
  const dropdownRef = useRef(null);
  const { pathname }  = useLocation();
  const { T }         = useTheme();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isToolActive = pathname.startsWith('/tools') ||
    pathname.startsWith('/job-search') ||
    pathname.startsWith('/freelancer') ||
    pathname.startsWith('/developer');

  return (
    <>
      <style>{`
        .navbar {
          position: sticky;
          top: 0;
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 32px;
          height: 60px;
          background: ${T.bg2};
          border-bottom: 1px solid ${T.border};
          transition: box-shadow .2s;
          font-family: "DM Sans", system-ui, sans-serif;
        }
        .navbar.scrolled {
          box-shadow: 0 4px 24px rgba(0,0,0,.18);
        }
        .nav-logo {
          display: flex;
          align-items: center;
          gap: 9px;
          text-decoration: none;
          flex-shrink: 0;
        }
        .nav-logo-img {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          object-fit: contain;
        }
        .nav-logo-text {
          font-weight: 800;
          font-size: 15.5px;
          color: ${T.white};
          letter-spacing: -.02em;
          font-family: "Syne", system-ui, sans-serif;
        }
        .nav-logo-text .tld { color: ${T.cyan}; }

        /* Desktop links */
        .nav-links {
          display: flex;
          align-items: center;
          gap: 4px;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .nav-links a {
          display: inline-flex;
          align-items: center;
          padding: 6px 11px;
          border-radius: 7px;
          font-size: 13.5px;
          color: ${T.muted};
          text-decoration: none;
          transition: all .15s;
          white-space: nowrap;
        }
        .nav-links a:hover,
        .nav-links a.active { color: ${T.white}; background: rgba(255,255,255,.06); }
        .nav-links a.nav-cta {
          background: linear-gradient(135deg, ${T.cyan}, ${T.blue});
          color: #07080c !important;
          font-weight: 700;
          padding: 6px 14px;
        }
        .nav-links a.nav-cta:hover { opacity: .85; background: linear-gradient(135deg, ${T.cyan}, ${T.blue}); }

        /* Tools dropdown trigger */
        .nav-tools-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 6px 11px;
          border-radius: 7px;
          font-size: 13.5px;
          color: ${isToolActive ? T.white : T.muted};
          background: ${isToolActive ? 'rgba(255,255,255,.06)' : 'transparent'};
          border: none;
          cursor: pointer;
          font-family: "DM Sans", system-ui, sans-serif;
          transition: all .15s;
          white-space: nowrap;
        }
        .nav-tools-btn:hover {
          color: ${T.white};
          background: rgba(255,255,255,.06);
        }
        .nav-tools-btn .chevron {
          font-size: 9px;
          transition: transform .2s;
          opacity: .6;
        }
        .nav-tools-btn .chevron.open { transform: rotate(180deg); }

        /* Dropdown panel */
        .nav-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          width: 680px;
          background: ${T.bg2};
          border: 1px solid ${T.border};
          border-radius: 14px;
          padding: 20px;
          box-shadow: 0 16px 48px rgba(0,0,0,.35);
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          z-index: 300;
          animation: ddFadeIn .15s ease;
        }
        @keyframes ddFadeIn {
          from { opacity:0; transform:translateX(-50%) translateY(-6px); }
          to   { opacity:1; transform:translateX(-50%) translateY(0); }
        }
        .nav-dd-group-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .1em;
          color: ${T.dim};
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid ${T.border};
        }
        .nav-dd-item {
          display: flex;
          flex-direction: column;
          padding: 8px 10px;
          border-radius: 8px;
          text-decoration: none;
          transition: all .12s;
          margin-bottom: 2px;
        }
        .nav-dd-item:hover {
          background: rgba(255,255,255,.05);
        }
        .nav-dd-item-label {
          font-size: 13px;
          font-weight: 600;
          color: ${T.white};
          margin-bottom: 2px;
        }
        .nav-dd-item-desc {
          font-size: 11.5px;
          color: ${T.dim};
        }
        .nav-dd-item:hover .nav-dd-item-label { color: ${T.cyan}; }

        /* Hamburger */
        .nav-ham {
          display: none;
          flex-direction: column;
          gap: 5px;
          padding: 6px;
          background: transparent;
          border: none;
          cursor: pointer;
        }
        .nav-ham span {
          display: block;
          width: 22px;
          height: 2px;
          background: ${T.muted};
          border-radius: 2px;
          transition: all .2s;
        }

        /* Mobile menu */
        .nav-mobile {
          display: none;
          flex-direction: column;
          background: ${T.bg2};
          border-bottom: 1px solid ${T.border};
          padding: 12px 20px 16px;
          gap: 2px;
          position: sticky;
          top: 60px;
          z-index: 199;
        }
        .nav-mobile.open { display: flex; }
        .nav-mobile a {
          padding: 9px 12px;
          border-radius: 8px;
          font-size: 14px;
          color: ${T.muted};
          text-decoration: none;
          transition: all .12s;
        }
        .nav-mobile a:hover { color: ${T.white}; background: rgba(255,255,255,.05); }
        .nav-mobile a.nav-cta {
          background: linear-gradient(135deg,${T.cyan},${T.blue});
          color: #07080c !important;
          font-weight: 700;
          text-align: center;
          margin-top: 6px;
        }
        .nav-mobile-group-label {
          font-size: 10.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .1em;
          color: ${T.dim};
          padding: 10px 12px 4px;
        }
        .nav-mobile-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 9px 12px;
          border-radius: 8px;
          font-size: 14px;
          color: ${T.muted};
          background: transparent;
          border: none;
          cursor: pointer;
          font-family: "DM Sans", system-ui, sans-serif;
          width: 100%;
          text-align: left;
        }
        .nav-mobile-toggle:hover { color: ${T.white}; background: rgba(255,255,255,.05); }

        @media (max-width: 900px) {
          .nav-links { display: none; }
          .nav-ham   { display: flex; }
        }
      `}</style>

      <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
        {/* Logo */}
        <Link to="/" className="nav-logo">
          <img src={logo} alt="Tylented logo" className="nav-logo-img"/>
          <span className="nav-logo-text">
            Tylent<span className="tld">ed</span> Tools
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="nav-links">
          {TOP_LINKS.map(({ to, label }) => (
            <li key={to}>
              <Link to={to} className={pathname === to ? 'active' : ''}>
                {label}
              </Link>
            </li>
          ))}

          {/* Tools dropdown */}
          <li ref={dropdownRef} style={{ position:'relative' }}>
            <button
              className="nav-tools-btn"
              onClick={() => setDropdownOpen(o => !o)}
            >
              🛠️ Tools
              <span className={`chevron${dropdownOpen ? ' open' : ''}`}>▼</span>
            </button>

            {dropdownOpen && (
              <div className="nav-dropdown">
                {TOOL_GROUPS.map(group => (
                  <div key={group.label}>
                    <div className="nav-dd-group-label">{group.label}</div>
                    {group.items.map(item => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className="nav-dd-item"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="nav-dd-item-label">{item.label}</span>
                        <span className="nav-dd-item-desc">{item.desc}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </li>

          <li><NavUserMenu/></li>
          <li>
            <Link to="https://arhatinfo.com" className="nav-cta" target="_blank" rel="noreferrer">
              Tylented Business
            </Link>
          </li>
          {/* <li><ThemeToggle/></li> */}
        </ul>

        {/* Hamburger */}
        <button
          className="nav-ham"
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Menu"
        >
          <span/><span/><span/>
        </button>
      </nav>

      {/* Mobile menu */}
      <div className={`nav-mobile${mobileOpen ? ' open' : ''}`}>
        {TOP_LINKS.map(({ to, label }) => (
          <Link key={to} to={to}>{label}</Link>
        ))}

        {/* Mobile tools accordion */}
        <button
          className="nav-mobile-toggle"
          onClick={() => setMobileTools(o => !o)}
        >
          🛠️ Tools
          <span style={{ fontSize:10, opacity:.6 }}>{mobileTools ? '▲' : '▼'}</span>
        </button>

        {mobileTools && TOOL_GROUPS.map(group => (
          <div key={group.label}>
            <div className="nav-mobile-group-label">{group.label}</div>
            {group.items.map(item => (
              <Link
                key={item.to}
                to={item.to}
                style={{ paddingLeft:24 }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        ))}

        <Link
          to="https://arhatinfo.com"
          className="nav-cta"
          target="_blank"
          rel="noreferrer"
          style={{ marginTop:'.5rem' }}
        >
          Tylented Business
        </Link>
        <div style={{ padding:'8px 4px' }}>
          {/* <ThemeToggle/> */}
        </div>
      </div>
    </>
  );
}